import { VideoRoomSignaling } from 'infrastructure/janus';
import { RoomId, Publisher } from 'infrastructure/janus/signaling/VideoRoom';
import { VideoRoomSubscriber } from 'infrastructure/janus/signaling/VideoRoom/subscriber';
import { hasUnderlying, MediaStreamConnection, MediaStreamConnector, RecordableConnector, StatsReportableConnector } from './MediaStreamConnector';
import { ReconnectableConnector } from './ReconnectableConnector';
import { createListenerManager } from 'infrastructure/utility/ListenerManager';
import { VideoRoomParticipant } from 'infrastructure/janus/signaling/VideoRoom/participant';

export type JanusConnectionParams = {
    signalingServer: string;
    room: RoomId;
    username: string;
    config: {
        iceServers?: { urls: string }[];
        encodedInsertableStreams?: boolean;
    };
}

export class JanusVideoRoomConnector implements MediaStreamConnector, RecordableConnector, StatsReportableConnector, ReconnectableConnector {
    private readonly signaling = new VideoRoomSignaling();
    private readonly params: JanusConnectionParams;
    private targetPublisher: Publisher | undefined;
    private _sub: VideoRoomSubscriber | undefined;

    constructor(params: JanusConnectionParams) {
        this.params = params;
    }

    getParams() { return this.params; }

    private participant: VideoRoomParticipant | null = null;

    async connect(params: { audio: boolean }) {
        const { signalingServer, room, username, config } = this.params;
        const { audio } = params;

        const pluginHandler = await this.signaling.create(signalingServer);

        const knownPublishers = new Array<Publisher>();
        try {
            this.participant = await this.signaling.waitForPublishers(
                pluginHandler,
                room,
                username,
                event => {
                    switch (event.event) {
                        case 'published':
                            {
                                const { publishers } = event;
                                // insert new (unknown) publishers at the front of knownPublishers
                                publishers.forEach(publisher => {
                                    if (!knownPublishers.find(x => x.id === publisher.id)) {
                                        knownPublishers.unshift(publisher);
                                    }
                                });
                            }
                            break;
                        case 'unpublished':
                            {
                                const { publisherId } = event;
                                // remove publisher from knownPublishers
                                const index = knownPublishers.findIndex(x => x.id === publisherId);
                                if (index >= 0) {
                                    knownPublishers.splice(index, 1);
                                }
                            }
                            break;
                    }

                    // if the target has changed?
                    const newTarget = knownPublishers.at(0);
                    if (this.targetPublisher === newTarget) return;
                    this.targetPublisher = newTarget;

                    // if we have new target, subscribe it
                    if (newTarget) {
                        const { connection: { privateId } } = event;
                        // intentionally not awaiting here
                        (async () => {
                            // ok, let's subscribe the new target
                            const sub = await this.signaling.prepareSubscribe(pluginHandler);

                            // the target has been changed while preparing subscription
                            if (this.targetPublisher !== newTarget) {
                                await sub.leave();
                                return;
                            }

                            // open stream
                            const stream = await this._connectStream(
                                sub,
                                config,
                                room,
                                privateId,
                                newTarget,
                                audio
                            );

                            // the target has been changed while preparing stream
                            if (this.targetPublisher !== newTarget) {
                                await sub.leave();
                                return;
                            }

                            // close current subscription if we have (intentionally not awaiting here)
                            void this._sub?.leave();
                            // update current subscription
                            this._sub = sub;

                            // notify new stream
                            this.reconnectListeners.forEach(listener => listener({
                                stream,
                                lookupReceiver: track => sub.receivers.find(r => r.track === track),
                            }));
                        })();
                    } else {
                        // close current subscription if we have (intentionally not awaiting here)
                        void this._sub?.leave();
                        this._sub = undefined;
                    }
                }
            );
        } catch (reason) {
            console.error('cannot start waiting for publishers', reason);
        }

        return new Promise<MediaStreamConnection>(resolve => {
            const listener = (connection: MediaStreamConnection) => {
                this.removeReconnectListener(listener);
                resolve(connection);
            };
            this.addReconnectListener(listener);
        });
    }

    async disconnect() {
        const participant = this.participant;
        this.participant = null;
        if (!participant) return;
        await participant.leave();
    }

    async enableRecording(enabled: boolean) {
        const { room } = this.params;
        await this._sub?.enableRecording(room, enabled);
    }

    async getStats(selector?: MediaStreamTrack): Promise<RTCStatsReport | undefined> {
        return await this._sub?.getStats(selector);
    }

    private readonly reconnectListeners = createListenerManager<(connection: MediaStreamConnection) => void>();

    addReconnectListener(listener: (connection: MediaStreamConnection) => void): void {
        this.reconnectListeners.addListener(listener);
    }

    removeReconnectListener(listener: (connection: MediaStreamConnection) => void): void {
        this.reconnectListeners.removeListener(listener);
    }

    private _connectStream(
        sub: VideoRoomSubscriber,
        config: RTCConfiguration,
        room: RoomId,
        privateId: string | undefined,
        publisher: Publisher,
        audio: boolean
    ) {
        return new Promise<MediaStream>(resolve => {
            this.signaling.subscribe(
                sub,
                config,
                room,
                publisher,
                privateId,
                (stream, kind) => {
                    if (kind === 'audio' && !audio) return;
                    resolve(stream);
                },
            );
        });
    }
}

export function getJanusVideoRoomConnector(x: MediaStreamConnector): JanusVideoRoomConnector | undefined {
    if (x instanceof JanusVideoRoomConnector) {
        return x;
    }
    if (hasUnderlying(x)) {
        return getJanusVideoRoomConnector(x.underlying);
    }
    return undefined;
}
